import React, { useState, useEffect } from 'react';
import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  startOfWeek, 
  endOfWeek, 
  eachDayOfInterval, 
  isSameMonth, 
  isSameDay, 
  addMonths, 
  subMonths, 
  isWithinInterval, 
  parseISO, 
  isToday 
} from 'date-fns';
import DashboardLayout from '../components/DashboardLayout';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { 
  FiCalendar, FiList, FiSearch, FiCheck, FiClock, FiX, 
  FiEdit2, FiTrash2, FiDownload, FiUpload, FiChevronLeft, FiChevronRight 
} = FiIcons;

const ReservationsPage = () => {
  const [viewMode, setViewMode] = useState('calendar');
  const [reservations, setReservations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
    const today = new Date();
    const mockReservations = [
      { id: '1', guestName: 'John Doe', guestEmail: 'john@example.com', checkIn: '2024-01-15', checkOut: '2024-01-18', venue: 'Luxury Palace', status: 'confirmed', totalPrice: 450 },
      { id: '5', guestName: 'Charlie Wilson', checkIn: format(today, 'yyyy-MM-05'), checkOut: format(today, 'yyyy-MM-08'), venue: 'Luxury Palace', status: 'confirmed', totalPrice: 450 },
      { id: '6', guestName: 'Diana Prince', checkIn: format(today, 'yyyy-MM-15'), checkOut: format(today, 'yyyy-MM-20'), venue: 'Luxury Palace', status: 'confirmed', totalPrice: 750 }
    ];
    setReservations(mockReservations);
  }, []);

  const getEventsForDay = (day) => {
    return reservations.filter(res => {
      const checkIn = parseISO(res.checkIn);
      const checkOut = parseISO(res.checkOut);
      return (isWithinInterval(day, { start: checkIn, end: checkOut }) || isSameDay(day, checkIn)) &&
             (searchTerm === '' || res.guestName.toLowerCase().includes(searchTerm.toLowerCase()));
    });
  };

  const renderMonthGrid = (monthDate) => {
    const days = eachDayOfInterval({
      start: startOfWeek(startOfMonth(monthDate)),
      end: endOfWeek(endOfMonth(monthDate))
    });

    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-4 bg-gray-50 border-b border-gray-200 text-center">
          <h3 className="font-bold text-gray-900">{format(monthDate, 'MMMM yyyy')}</h3>
        </div>
        <div className="grid grid-cols-7 bg-gray-100 border-b border-gray-200">
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(d => (
            <div key={d} className="py-2 text-center text-[10px] font-bold text-gray-400 uppercase">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-px bg-gray-200">
          {days.map((day, idx) => {
            const events = getEventsForDay(day);
            const isCurrentMonth = isSameMonth(day, monthDate);
            return (
              <div key={idx} className={`min-h-[80px] p-1 bg-white ${!isCurrentMonth ? 'opacity-30' : ''}`}>
                <span className={`text-[10px] font-medium h-5 w-5 flex items-center justify-center rounded-full ${isToday(day) ? 'bg-blue-600 text-white' : 'text-gray-500'}`}>
                  {format(day, 'd')}
                </span>
                <div className="mt-1 space-y-0.5">
                  {events.map(event => (
                    <div key={event.id} className="text-[9px] px-1 py-0.5 rounded bg-blue-100 text-blue-700 truncate border border-blue-200">
                      {event.guestName}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Reservations</h1>
            <p className="text-gray-600">3-Month Planning View</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} className="p-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
              <SafeIcon icon={FiChevronLeft} className="h-5 w-5" />
            </button>
            <button onClick={() => setCurrentMonth(new Date())} className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium">Today</button>
            <button onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} className="p-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
              <SafeIcon icon={FiChevronRight} className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-gray-200 flex flex-wrap gap-4 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-2.5 text-gray-400" />
            <input 
              type="text" 
              placeholder="Filter guests..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <div className="flex items-center bg-gray-100 p-1 rounded-lg">
            <button onClick={() => setViewMode('list')} className={`px-4 py-1.5 rounded-md text-sm font-medium ${viewMode === 'list' ? 'bg-white shadow-sm' : 'text-gray-500'}`}>List</button>
            <button onClick={() => setViewMode('calendar')} className={`px-4 py-1.5 rounded-md text-sm font-medium ${viewMode === 'calendar' ? 'bg-white shadow-sm' : 'text-gray-500'}`}>Calendar</button>
          </div>
        </div>

        {viewMode === 'calendar' ? (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            {renderMonthGrid(currentMonth)}
            {renderMonthGrid(addMonths(currentMonth, 1))}
            {renderMonthGrid(addMonths(currentMonth, 2))}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
             {/* List View Placeholder */}
             <p className="p-8 text-center text-gray-500">Switch to Calendar for the 3-month overview.</p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ReservationsPage;